import {
  styled,
  Avatar,
  Backdrop,
  Button,
  ListItem,
  ListItemAvatar,
  Paper,
  List,
  ListItemText,
  CircularProgress,
  IconButton,
} from "@mui/material";
import cross from "components/icons/cross-black.svg";
import colors from "../../../theme/themeConstants";
import React, { Fragment, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IAppState } from "../../../store/app-state";
import { addGifToCollection, fetchMyCollections, setEmptyCollections } from "../../../store/collections/collections-action";
import { CreateNewCollectionPopup } from "./CreateNewCollectionPopup";
import { useKeyPress } from "../../../hooks/useKeyPress";
import { useIsAuth } from "../../../hooks/useIsAuth";
import { showAuthModal } from "store/application/application-action";
import { metrics } from "utils/metrics";

interface IProps {
  isOpen: boolean;
  togglePopup: (event: React.MouseEvent) => void;
  gifId: string;
}

const AddToCollectionPopup = ({ isOpen, togglePopup, gifId }: IProps) => {
  const collections = useSelector((state: IAppState) => state.collections.collections);
  const isLoading = useSelector((state: IAppState) => state.collections.loading);
  const [isNewCollectionPopupOpen, setIsOpenNewCollectionPopup] = useState(false);

  const escPressed = useKeyPress("Escape");

  const isAuth = useIsAuth();

  const dispatch = useDispatch();

  const toggleCreateNewCollectionPopup = () => {
    setIsOpenNewCollectionPopup(prev => !prev);
  };

  const stopBackdropPropagation = (event: React.MouseEvent) => event.stopPropagation();

  const addToCollections = async (folderId: string) => {
    dispatch(addGifToCollection({
      collectionId: folderId,
      gifId: gifId
    }));
    await metrics.sendAddedToCollection({ gif: gifId });
    // @ts-ignore
    togglePopup();
  };

  const handleLogin = () => {
    dispatch(showAuthModal());
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchMyCollections({
        page: 1,
        count: 100
      }));
    }
    return () => {
      dispatch(setEmptyCollections());
    };
    // eslint-disable-next-line
  }, [isOpen])

  useEffect(() => {
    if (escPressed) {
      // @ts-ignore
      togglePopup();
    }
    // eslint-disable-next-line
  }, [escPressed])

  return (
    <Backdrop open={isOpen} id={"add-to-collection-popupOpen"}
      sx={{
        zIndex: "var(--popupZ)"
      }}
      onClick={stopBackdropPropagation}
    >
      <CreateNewCollectionPopup isOpen={isNewCollectionPopupOpen} togglePopup={toggleCreateNewCollectionPopup} />
      <Paper
        sx={{
          maxHeight: "90vh",
          maxWidth: "420px",
          width: "100%",
          padding: "24px 30px 0",
          borderRadius: "24px",
          overflow: "auto",
          zIndex: 22,
          position: "relative",
          "@media screen and (max-width: 767px)": {
            maxHeight: "95vh",
            maxWidth: "90%",
            minHeight: "70vh",
            padding: "1rem"
          }
        }}
      >
        <PopupSubheader>
          <h3>Add to collection</h3>
          <Button onClick={togglePopup}><img src={cross} alt="close popup" /></Button>
        </PopupSubheader>
        {isAuth ? (
          <Fragment>
            <List
              sx={{
                width: "100%",
                margin: "0",
                padding: "0",
                overflowY: "auto",
                maxHeight: "50vh",
                display: "flex",
                flexDirection: "column",
                color: "black !important",
                paddingBottom: "5rem",
                "& div p": {
                  color: "darkgray",
                },
              }}
            >
              {isLoading ? <CircularProgress sx={{ color: colors.redPink, margin: "auto" }}/> : (
                collections ? collections.map((collection, index) => (
                  <ListItem
                    key={index}
                    onClick={() => addToCollections(collection.folderId)}
                    sx={{
                      padding: "0.5rem",
                      backgroundColor: "#F3F5F9",
                      borderRadius: "16px",
                      marginTop: "8px",
                      cursor: "pointer",
                      transition: "all .3s ease",
                      "&:hover": {
                        backgroundColor: "#E5E9F1",
                      }
                    }}
                  >
                    <ListItemAvatar sx={{ marginRight: "16px" }}>
                      <Avatar sx={{ width: 48, height: 48, borderRadius: "16px" }} src={collection.thumbs}>
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={collection.folderName}
                      sx={{
                        "span": {
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: 16,
                          fontWeight: 500,
                          lineHeight: "24px"
                        }
                      }}
                    />
                  </ListItem>
                )) : <p>We have some issues with loading collections. Please close and open the dialog and try again.</p>
              )}
            </List>
            <StyledLinkButton>
              <span>Create a new collection</span>
              <IconButton onClick={toggleCreateNewCollectionPopup} />
            </StyledLinkButton>
          </Fragment>
        )
          : (
            <Fragment>
              <p>You’ve gotta log in to add to collections to RedGIFs!</p>
              <Button variant="contained" fullWidth sx={{
                background: colors.purpleGradient,
                margin: "2rem 0 1rem 0",
                minHeight: "3rem",
                borderRadius: "38px",
              }} onClick={handleLogin}>
                Login
              </Button>
            </Fragment>
          )
        }
      </Paper>
    </Backdrop >
  );
};

export default AddToCollectionPopup;

const PopupSubheader = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
  "& h3": {
    color: "black !important",
    fontSize: "20px",
    fontWeight: "500",
  },
  "& button": {
    padding: "6px 0",
    minWidth: "32px",
  },
  "& img": {
    width: "32px",
    height: "32px",
  },
});

const StyledLinkButton = styled("div")({
  fontSize: "16px",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  textDecoration: "none",
  position: "absolute",
  bottom: 0,
  left: 0,
  zIndex: 20,
  height: "72px",
  background: "#fff",
  borderBottomRightRadius: 24,
  borderBottomLeftRadius: 24,
  padding: "0 30px",

  "& button": {
    // eslint-disable-next-line max-len
    backgroundImage: "url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9InVybCgjcGFpbnQwX2xpbmVhcl80MzM3XzE5NDgzNykiLz4KPHBhdGggZD0iTTE2IDkuNTk5NjFMMTYgMjIuMzk5NiIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8cGF0aCBkPSJNMjIuMzk5OSAxNkw5LjU5OTkgMTYiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiLz4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0icGFpbnQwX2xpbmVhcl80MzM3XzE5NDgzNyIgeDE9IjE2IiB5MT0iMCIgeDI9IjE2IiB5Mj0iMzIiIGdyYWRpZW50VW5pdHM9InVzZXJTcGFjZU9uVXNlIj4KPHN0b3Agc3RvcC1jb2xvcj0iIzhGMkNGMiIvPgo8c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiNDRDRDRkYiLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgo8L3N2Zz4K)",
    backgroundPosition: "center",
    backgrounfRepeat: "no-repeat",
    width: 32,
    height: 32
  },
  "& img": {
    width: "15px"
  }
});
